import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link, useLocation } from 'react-router';
import { MapPin, Loader2, Navigation, Sparkles, ArrowRight, ChevronDown, Clock, Route, MessageCircle, Mail, Check, Calendar as CalendarIcon, Clock as ClockIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '../lib/utils';

/* ═══ BOOKING CONFIG ═══ */
const WHATSAPP_NUMBER = '27785870868'; // South Africa format without +
const BOOKING_EMAIL = 'rushncube@support.skivatours.com';

async function searchGooglePlaces(query: string): Promise<AddressSuggestion[]> {
  const res = await fetch(`/api/maps/autocomplete?q=${encodeURIComponent(query)}`);
  if (!res.ok) {
    throw new Error('Google Places search failed');
  }

  const predictions = await res.json();
  return (predictions ?? []).map((prediction: { description?: string; place_id?: string | number }) => ({
    display_name: prediction.description ?? '',
    place_id: String(prediction.place_id ?? ''),
  }));
}

async function getGooglePlaceLocation(placeId: string): Promise<Location> {
  const res = await fetch(`/api/maps/place-details?placeId=${encodeURIComponent(placeId)}`);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Google Place details failed: ${body}`);
  }

  const data = await res.json();
  if (!data?.result?.geometry?.location) {
    throw new Error('Google Place details failed');
  }

  return {
    display: data.result.formatted_address,
    lat: data.result.geometry.location.lat,
    lon: data.result.geometry.location.lng,
  };
}

async function calculateRouteWithGoogle(pickup: Location, destination: Location) {
  const origin = `${pickup.lat},${pickup.lon}`;
  const dest = `${destination.lat},${destination.lon}`;
  const res = await fetch(`/api/maps/directions?origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}`);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Google Directions failed: ${body}`);
  }

  const data = await res.json();
  if (!data?.routes?.length || !data.routes[0].legs?.length) {
    throw new Error('Google Directions failed');
  }

  const leg = data.routes[0].legs[0];
  return {
    km: Math.round(leg.distance.value / 1000),
    mins: Math.round(leg.duration.value / 60),
  };
}

/* ═══ RATE TABLE ═══ */
const rateBrackets = [
  { min: 0, max: 15, sedan: 343.20, luxuryMpv: 456.00, minibus: 624.00 },
  { min: 16, max: 30, sedan: 686.40, luxuryMpv: 864.00, minibus: 1176.00 },
  { min: 31, max: 45, sedan: 1021.80, luxuryMpv: 1272.00, minibus: 1740.00 },
  { min: 46, max: 60, sedan: 1357.20, luxuryMpv: 1704.00, minibus: 2340.00 },
  { min: 61, max: 75, sedan: 1716.00, luxuryMpv: 2136.00, minibus: 2880.00 },
];

const SPECIAL_QUOTE_THRESHOLD = 75;

type VehicleType = 'sedan' | 'luxuryMpv' | 'minibus';

type ServiceType = 'airport' | 'corporate' | 'shuttle' | 'safari' | 'crossborder' | 'city';

interface Location {
  display: string;
  lat: number;
  lon: number;
}

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
  place_id: number;
}

type AddressSuggestion = NominatimResult | {
  display_name: string;
  place_id: string;
};


const services: { key: ServiceType; label: string }[] = [
  { key: 'airport', label: 'VIP Airport Transfer' },
  { key: 'corporate', label: 'Corporate Drop-off' },
  { key: 'shuttle', label: 'Premium Shuttle Service' },
  { key: 'safari', label: 'Luxury Safari Tour' },
  { key: 'city', label: 'Guided City Tour' },
  { key: 'crossborder', label: 'Cross-border Transport' },
];

const vehicles: { key: VehicleType; label: string; seats: string }[] = [
  { key: 'sedan', label: 'SEDAN', seats: '4-Seater' },
  { key: 'luxuryMpv', label: 'LUXURY MPV', seats: '8-Seater' },
  { key: 'minibus', label: 'MINIBUS', seats: '14-Seater' },
];

function getPrice(distanceKm: number, vehicle: VehicleType): number | null {
  const bracket = rateBrackets.find((b) => distanceKm >= b.min && distanceKm <= b.max);
  if (!bracket) return null;
  return bracket[vehicle];
}

/* ═══ ADDRESS INPUT w/ AUTOCOMPLETE ═══ */
function AddressInput({
  label,
  icon,
  value,
  onChange,
  onSelect,
  placeholder,
  id,
}: {
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  onSelect: (suggestion: AddressSuggestion) => void;
  placeholder: string;
  id: string;
}) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const search = useCallback(async (query: string) => {
    if (query.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      // Use backend autocomplete endpoint (uses Nominatim with fallback logic)
      const res = await fetch(`/api/maps/autocomplete?q=${encodeURIComponent(query)}`);
      if (!res.ok) throw new Error('Autocomplete failed');
      
      const data: AddressSuggestion[] = await res.json();
      setSuggestions(data);
      setShowDropdown(data.length > 0);
    } catch {
      setSuggestions([]);
      setShowDropdown(false);
    }
    setLoading(false);
  }, []);

  const handleChange = (v: string) => {
    onChange(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 500);
  };

  const handleSelect = (s: AddressSuggestion) => {
    const rawValue = value.trim();
    const hasRawNumber = /\b\d+\b/.test(rawValue);
    const suggestionHasNumber = /\b\d+\b/.test(s.display_name);
    const selectedText = hasRawNumber && !suggestionHasNumber ? rawValue : s.display_name;
    if (hasRawNumber && !suggestionHasNumber) {
      s.display_name = selectedText;
    }
    onChange(selectedText);
    onSelect(s);
    setShowDropdown(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div ref={wrapperRef} className="relative w-full">
      <label htmlFor={id} className="trip-calc-label">
        {label}
      </label>
      <div className="relative">
        <div className="trip-calc-input-icon">{icon}</div>
        <input
          id={id}
          type="text"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          autoComplete="off"
          className="trip-calc-input"
        />
        {loading && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <Loader2 className="w-4 h-4 text-[#8b7a3a] animate-spin" />
          </div>
        )}
      </div>

      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="trip-calc-suggestions"
          >
            {suggestions.map((s) => (
              <button
                type="button"
                key={s.place_id}
                onClick={() => handleSelect(s)}
                className="trip-calc-suggestion-item"
              >
                <MapPin className="w-4 h-4 text-[#8b7a3a] mt-0.5 shrink-0" />
                <span className="line-clamp-2">{s.display_name}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══ MAIN TRIP CALCULATOR ═══ */
export function TripCalculator() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  let initial = searchParams.get('service') as ServiceType | null;
  if (initial && (initial as string) === 'transfer') initial = 'airport';

  const [step, setStep] = useState<1 | 2>(1);
  const [service, setService] = useState<ServiceType>(
    initial && services.some(s => s.key === initial) ? initial : 'airport'
  );
  const [vehicle, setVehicle] = useState<VehicleType>('sedan');
  const [pickupStreetNumber, setPickupStreetNumber] = useState('');
  const [destStreetNumber, setDestStreetNumber] = useState('');
  const [pickupText, setPickupText] = useState('');
  const [destText, setDestText] = useState('');
  const [pickup, setPickup] = useState<Location | null>(null);
  const [destination, setDestination] = useState<Location | null>(null);
  const [tripDate, setTripDate] = useState('');
  const [tripTime, setTripTime] = useState('');
  const [guests, setGuests] = useState('1 Guest');
  const [requirements, setRequirements] = useState('');
  const [distanceKm, setDistanceKm] = useState<number | null>(null);
  const [durationMin, setDurationMin] = useState<number | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [showRates, setShowRates] = useState(location.pathname === '/book');
  const [booked, setBooked] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedHour, setSelectedHour] = useState('12');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const [googleMapsError] = useState<string | null>(null);

  // Compute price and outOfRange dynamically during rendering
  const price = distanceKm !== null ? getPrice(distanceKm, vehicle) : null;
  const outOfRange = fetchFailed || (distanceKm !== null && price === null);

  // sync service selection with URL search params without useEffect
  const [prevSearch, setPrevSearch] = useState(location.search);
  if (location.search !== prevSearch) {
    setPrevSearch(location.search);
    const params = new URLSearchParams(location.search);
    let s = params.get('service') as ServiceType | null;
    if (s && (s as string) === 'transfer') s = 'airport';
    if (s && services.some(item => item.key === s) && s !== service) {
      setService(s);
    }
  }

  // sync path changes
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  if (location.pathname !== prevPathname) {
    setPrevPathname(location.pathname);
    if (location.pathname === '/book') {
      setShowRates(true);
    }
  }

  const resetCalculator = () => {
    setStep(1);
    const params = new URLSearchParams(location.search);
    let s = params.get('service') as ServiceType | null;
    if (s && (s as string) === 'transfer') s = 'airport';
    setService(s && services.some(item => item.key === s) ? s : 'airport');
    setVehicle('sedan');
    setPickupText('');
    setDestText('');
    setPickup(null);
    setDestination(null);
    setPickupStreetNumber('');
    setDestStreetNumber('');
    setTripDate('');
    setTripTime('');
    setGuests('1 Guest');
    setRequirements('');
    setDistanceKm(null);
    setDurationMin(null);
    setFetchFailed(false);
    setBooked(false);
    setShowDatePicker(false);
    setShowTimePicker(false);
    setCalendarDate(new Date());
  };

  const handlePickupTextChange = (text: string) => {
    setPickupText(text);
    setBooked(false);
    if (pickup && text !== pickup.display) {
      setPickup(null);
      setDistanceKm(null);
      setDurationMin(null);
      setFetchFailed(false);
    }
  };

  const handleDestTextChange = (text: string) => {
    setDestText(text);
    setBooked(false);
    if (destination && text !== destination.display) {
      setDestination(null);
      setDistanceKm(null);
      setDurationMin(null);
      setFetchFailed(false);
    }
  };

  const resolveAddressSuggestion = async (suggestion: AddressSuggestion): Promise<Location> => {
    if ('lat' in suggestion && 'lon' in suggestion && suggestion.lat && suggestion.lon) {
      return {
        display: suggestion.display_name,
        lat: parseFloat(suggestion.lat),
        lon: parseFloat(suggestion.lon),
      };
    }

    if ('place_id' in suggestion && typeof suggestion.place_id === 'string') {
      try {
        return await getGooglePlaceLocation(suggestion.place_id);
      } catch {
        return await resolveTextLocation(suggestion.display_name);
      }
    }

    throw new Error('Unable to resolve address suggestion.');
  };

  const resolveTextLocation = async (text: string): Promise<Location> => {
    const trimmed = text.trim();
    if (trimmed.length < 3) {
      throw new Error('Location query too short.');
    }

    try {
      const suggestions = await searchGooglePlaces(trimmed);
      if (suggestions.length > 0) {
        return await resolveAddressSuggestion(suggestions[0]);
      }
    } catch {
      // fallback to Nominatim when Google search fails
    }

    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(trimmed)}&format=json&countrycodes=za&limit=1&addressdetails=1`,
      {
        headers: {
          'Accept-Language': 'en',
          'User-Agent': 'SkivaToursWebsite/1.0 (rushncube@support.skivatours.com)'
        }
      }
    );
    const nominatimData: NominatimResult[] = await res.json();
    if (!nominatimData || nominatimData.length === 0) {
      throw new Error('Unable to resolve address from text.');
    }

    const displayValue = /\b\d+\b/.test(trimmed) && !/\b\d+\b/.test(nominatimData[0].display_name)
      ? trimmed
      : nominatimData[0].display_name;

    return {
      display: displayValue,
      lat: parseFloat(nominatimData[0].lat),
      lon: parseFloat(nominatimData[0].lon),
    };
  };

  const handlePickupSelect = async (suggestion: AddressSuggestion) => {
    try {
      const resolved = await resolveAddressSuggestion(suggestion);
      const displayWithNumber = pickupStreetNumber && !/\b\d+\b/.test(resolved.display)
        ? `${pickupStreetNumber} ${resolved.display}`
        : (/\b\d+\b/.test(pickupText) && !/\b\d+\b/.test(resolved.display) ? pickupText : resolved.display);
      setPickup({ ...resolved, display: displayWithNumber });
      setPickupText(displayWithNumber);
      setBooked(false);
    } catch {
      setFetchFailed(true);
    }
  };

  const handleDestSelect = async (suggestion: AddressSuggestion) => {
    try {
      const resolved = await resolveAddressSuggestion(suggestion);
      const displayWithNumber = destStreetNumber && !/\b\d+\b/.test(resolved.display)
        ? `${destStreetNumber} ${resolved.display}`
        : (/\b\d+\b/.test(destText) && !/\b\d+\b/.test(resolved.display) ? destText : resolved.display);
      setDestination({ ...resolved, display: displayWithNumber });
      setDestText(displayWithNumber);
      setBooked(false);
    } catch {
      setFetchFailed(true);
    }
  };

  const handleServiceChange = (s: ServiceType) => {
    setService(s);
    setBooked(false);
  };

  const handleVehicleChange = (v: VehicleType) => {
    setVehicle(v);
    setBooked(false);
  };

  const handleGuestsChange = (g: string) => {
    setGuests(g);
    setBooked(false);
  };

  const handleRequirementsChange = (r: string) => {
    setRequirements(r);
    setBooked(false);
  };

  const handleOpenTimePicker = () => {
    if (tripTime) {
      const parts = tripTime.split(':');
      if (parts.length === 2) {
        setSelectedHour(parts[0]);
        setSelectedMinute(parts[1]);
      }
    }
    setShowTimePicker(!showTimePicker);
    setShowDatePicker(false);
  };

  const bookedDates = [
    '2026-05-19',
    '2026-05-22',
    '2026-05-25',
    '2026-06-04',
    '2026-06-11',
    '2026-06-18',
    '2026-06-25',
  ];

  const getMonthData = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1).getDay(); // 0 (Sun) to 6 (Sat)
    const daysInMonth = new Date(year, month + 1, 0).getDate(); // 28-31
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const monthStr = String(month + 1).padStart(2, '0');
      const dayStr = String(i).padStart(2, '0');
      const dateString = `${year}-${monthStr}-${dayStr}`;
      days.push({
        day: i,
        dateString,
        isBooked: bookedDates.includes(dateString),
        isPast: new Date(year, month, i) < today
      });
    }
    return days;
  };

  const handlePrevMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
  };

  const buildBookingMessage = () => {
    const serviceName = services.find(s => s.key === service)?.label || service;
    const vehicleInfo = vehicles.find(v => v.key === vehicle);
    const vehicleName = vehicleInfo ? `${vehicleInfo.label} (${vehicleInfo.seats})` : vehicle;
    const priceStr = price ? `R${price.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : 'N/A';
    
    return [
      `🚖 *SKIVA AFRICA TOURS — New Booking Request*`,
      ``,
      `📋 *Service:* ${serviceName}`,
      `🚗 *Vehicle:* ${vehicleName}`,
      `📍 *Pickup:* ${pickupStreetNumber ? `${pickupStreetNumber} ${pickupText}` : pickupText}`,
      `📍 *Destination:* ${destStreetNumber ? `${destStreetNumber} ${destText}` : destText}`,
      `📅 *Date:* ${tripDate || 'Not specified'}`,
      `⏰ *Time:* ${tripTime || 'Not specified'}`,
      `👥 *Guests:* ${guests}`,
      `📝 *Requirements:* ${requirements || 'None'}`,
      `📏 *Distance:* ${distanceKm} km`,
      `⏱️ *Est. Duration:* ${durationMin} min`,
      `💰 *Estimated Fare:* ${priceStr}`,
      ``,
      `Please confirm availability and finalise this booking.`,
    ].join('\n');
  };

  const handleWhatsAppBooking = () => {
    const message = buildBookingMessage();
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setBooked(true);
  };

  const handleEmailBooking = () => {
    const serviceName = services.find(s => s.key === service)?.label || service;
    const vehicleInfo = vehicles.find(v => v.key === vehicle);
    const vehicleName = vehicleInfo ? `${vehicleInfo.label} (${vehicleInfo.seats})` : vehicle;
    const priceStr = price ? `R${price.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : 'N/A';
    
    const subject = `Skiva Africa Tours — Booking Request (${serviceName})`;
    const body = [
      `SKIVA AFRICA TOURS — New Booking Request`,
      ``,
      `Service: ${serviceName}`,
      `Vehicle: ${vehicleName}`,
      `Pickup: ${pickupStreetNumber ? `${pickupStreetNumber} ${pickupText}` : pickupText}`,
      `Destination: ${destStreetNumber ? `${destStreetNumber} ${destText}` : destText}`,
      `Date: ${tripDate || 'Not specified'}`,
      `Time: ${tripTime || 'Not specified'}`,
      `Guests: ${guests}`,
      `Requirements: ${requirements || 'None'}`,
      `Distance: ${distanceKm} km`,
      `Est. Duration: ${durationMin} min`,
      `Estimated Fare: ${priceStr}`,
      ``,
      `Please confirm availability and finalise this booking.`,
    ].join('\n');
    
    const url = `mailto:${BOOKING_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.assign(url);
    setBooked(true);
  };

  const getWhatsAppQuoteMessage = () => {
    const serviceName = services.find(s => s.key === service)?.label || service;
    const vehicleInfo = vehicles.find(v => v.key === vehicle);
    const vehicleName = vehicleInfo ? `${vehicleInfo.label} (${vehicleInfo.seats})` : vehicle;

    return [
      `🚖 *SKIVA AFRICA TOURS — Special Quote Request*`,
      ``,
      `📋 *Service:* ${serviceName}`,
      `🚗 *Preferred Vehicle:* ${vehicleName}`,
      `📍 *Pickup:* ${pickupStreetNumber ? `${pickupStreetNumber} ${pickupText}` : pickupText}`,
      `📍 *Destination:* ${destStreetNumber ? `${destStreetNumber} ${destText}` : destText}`,
      `📏 *Distance:* ${distanceKm} km`,
      `⏱️ *Est. Duration:* ${durationMin ?? 'TBC'} min`,
      ``,
      `Please provide a tailored quote for this journey. Thank you.`,
    ].join('\n');
  };

  const handleWhatsAppQuote = () => {
    const message = getWhatsAppQuoteMessage();
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleContinue = () => {
    setStep(2);
  };

  const calculate = async () => {
    setCalculating(true);
    setFetchFailed(false);
    setDistanceKm(null);
    setDurationMin(null);

    let pickupPoint = pickup;
    let destinationPoint = destination;

    try {
      if (!pickupPoint) {
        const resolvedPickup = await resolveTextLocation(pickupText);
        pickupPoint = resolvedPickup;
        setPickup(resolvedPickup);
        setPickupText(resolvedPickup.display);
      }
      if (!destinationPoint) {
        const resolvedDestination = await resolveTextLocation(destText);
        destinationPoint = resolvedDestination;
        setDestination(resolvedDestination);
        setDestText(resolvedDestination.display);
      }

      if (!pickupPoint || !destinationPoint) {
        throw new Error('Pickup or destination is missing.');
      }

      const result = await calculateRouteWithGoogle(pickupPoint, destinationPoint);
      setDistanceKm(result.km);
      setDurationMin(result.mins);
    } catch {
      try {
        const resolvedPickup = pickupPoint ?? await resolveTextLocation(pickupText);
        const resolvedDestination = destinationPoint ?? await resolveTextLocation(destText);

        const res = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${resolvedPickup.lon},${resolvedPickup.lat};${resolvedDestination.lon},${resolvedDestination.lat}?overview=false`
        );
        const data = await res.json();
        if (data.routes && data.routes.length > 0) {
          const km = Math.round(data.routes[0].distance / 1000);
          const mins = Math.round(data.routes[0].duration / 60);
          setDistanceKm(km);
          setDurationMin(mins);
        } else {
          setFetchFailed(true);
        }
      } catch {
        setFetchFailed(true);
      }
    }
    setCalculating(false);
  };

  return (
    <section id="trip-calculator" className="trip-calc-section">
      {/* background texture */}
      <div className="trip-calc-bg-overlay" />
      <div className="trip-calc-bg-grain" />

      <div className="container mx-auto px-4 lg:px-8 max-w-6xl relative z-10">
        {/* heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="mb-8 md:mb-10"
        >
          <h2 className="trip-calc-title">
            <span className="trip-calc-title-live">Live</span>{' '}
            <span className="trip-calc-title-text">Trip Calculator</span>
          </h2>
        </motion.div>

        {/* Step 1 — Service & Vehicle selection */}
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="trip-calc-card">
                <div className="trip-calc-card-inner">
                  {/* Service selector */}
                  <div className="trip-calc-field">
                    <label className="trip-calc-field-label">SERVICE</label>
                    <div className="trip-calc-select-wrapper">
                      <select
                        id="service-select"
                        value={service}
                        onChange={(e) => handleServiceChange(e.target.value as ServiceType)}
                        className="trip-calc-select"
                      >
                        {services.map((s) => (
                          <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                      </select>
                      <ChevronDown className="trip-calc-select-chevron" />
                    </div>
                  </div>

                  {/* Vehicle Type cards */}
                  <div className="trip-calc-vehicle-group">
                    <label className="trip-calc-field-label">VEHICLE TYPE</label>
                    <div className="trip-calc-vehicle-cards">
                      {vehicles.map((v) => (
                        <motion.button
                          key={v.key}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleVehicleChange(v.key)}
                          className={cn(
                            'trip-calc-vehicle-card',
                            vehicle === v.key && 'trip-calc-vehicle-card--active'
                          )}
                        >
                          <span className="trip-calc-vehicle-label">{v.label}</span>
                          <span className="trip-calc-vehicle-seats">{v.seats}</span>
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Continue button */}
                  <div className="trip-calc-continue-wrap">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={handleContinue}
                      className="trip-calc-continue-btn"
                    >
                      CONTINUE
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* carousel indicator dots */}
              <div className="trip-calc-dots">
                <span className="trip-calc-dot trip-calc-dot--active" />
                <span className="trip-calc-dot" />
              </div>
            </motion.div>
          )}

          {/* Step 2 — Location inputs & price */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="trip-calc-card trip-calc-card--step2">
                {/* back button */}
                <button
                  onClick={() => { setStep(1); setDistanceKm(null); setFetchFailed(false); }}
                  className="trip-calc-back-btn"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" />
                  Back
                </button>

                {/* summary of selections */}
                <div className="trip-calc-selection-summary">
                  <div className="trip-calc-summary-chip">
                    {services.find(s => s.key === service)?.label}
                  </div>
                  <div className="trip-calc-summary-chip">
                    {vehicles.find(v => v.key === vehicle)?.label} · {vehicles.find(v => v.key === vehicle)?.seats}
                  </div>
                </div>

                {/* location inputs */}
                <div className="trip-calc-locations">
                  <div className="trip-calc-field">
  <label className="trip-calc-label">STREET NUMBER (PICKUP)</label>
  <input
    className="trip-calc-input"
    type="text"
    placeholder="e.g. 14"
    value={pickupStreetNumber}
    onChange={(e) => setPickupStreetNumber(e.target.value)}
  />
</div>
                  <div className="trip-calc-field">
                    <label className="trip-calc-label">STREET NUMBER (DESTINATION)</label>
                    <input
                      className="trip-calc-input"
                      type="text"
                      placeholder="e.g. 14"
                      value={destStreetNumber}
                      onChange={(e) => setDestStreetNumber(e.target.value)}
                    />
                  </div>
                  {googleMapsError && (
                    <p className="text-xs text-red-300 mb-3">Google Maps autocomplete could not load: {googleMapsError}</p>
                  )}
                  <AddressInput
                    id="pickup-input"
                    label="Pickup Location"
                    icon={<Navigation className="w-5 h-5" />}
                    value={pickupText}
                    onChange={handlePickupTextChange}
                    onSelect={handlePickupSelect}
                    placeholder="e.g. Jewel City, Johannesburg"
                  />
                  <AddressInput
                    id="destination-input"
                    label="Destination"
                    icon={<MapPin className="w-5 h-5" />}
                    value={destText}
                    onChange={handleDestTextChange}
                    onSelect={handleDestSelect}
                    placeholder="e.g. OR Tambo Airport"
                  />
                </div>

                {/* exact trip details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                  {/* Custom Date Picker */}
                  <div className="relative">
                    <label className="trip-calc-label">Date</label>
                    <div className="relative">
                      <input
                        type="text"
                        readOnly
                        value={tripDate}
                        onClick={() => { setShowDatePicker(!showDatePicker); setShowTimePicker(false); }}
                        placeholder="yyyy/mm/dd"
                        className="trip-calc-input cursor-pointer pr-10"
                      />
                      <CalendarIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                    </div>

                    <AnimatePresence>
                      {showDatePicker && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute left-0 top-full mt-2 w-full sm:w-80 bg-[#141414] border border-white/10 rounded-sm shadow-2xl z-50 p-4"
                        >
                          <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-3">
                            <button
                              type="button"
                              onClick={handlePrevMonth}
                              className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-gold-base hover:text-black transition-colors text-white"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="text-white font-bold text-sm tracking-wider uppercase">
                              {calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                            </span>
                            <button
                              type="button"
                              onClick={handleNextMonth}
                              className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-gold-base hover:text-black transition-colors text-white"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-7 gap-1 text-center mb-2">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                              <span key={d} className="text-[10px] font-bold text-gold-base uppercase tracking-wider py-1">
                                {d}
                              </span>
                            ))}
                          </div>

                          <div className="grid grid-cols-7 gap-1 text-center">
                            {getMonthData(calendarDate.getFullYear(), calendarDate.getMonth()).map((cell, idx) => {
                              if (!cell) {
                                return <div key={`empty-${idx}`} className="py-2" />;
                              }
                              return (
                                <button
                                  key={cell.dateString}
                                  type="button"
                                  disabled={cell.isBooked || cell.isPast}
                                  onClick={() => { setTripDate(cell.dateString); setBooked(false); setShowDatePicker(false); }}
                                  className={cn(
                                    "py-2 text-xs rounded-sm transition-all relative group font-medium",
                                    cell.isBooked && "bg-red-500/20 text-red-300 line-through cursor-not-allowed border border-red-500/30",
                                    cell.isPast && !cell.isBooked && "text-white/20 cursor-not-allowed",
                                    !cell.isBooked && !cell.isPast && "text-white hover:bg-gold-base hover:text-black cursor-pointer",
                                    tripDate === cell.dateString && "bg-gold-base text-black font-bold"
                                  )}
                                  title={cell.isBooked ? "Already Booked" : cell.isPast ? "Past Date" : "Available"}
                                >
                                  {cell.day}
                                  {cell.isBooked && (
                                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-red-500 rounded-full" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Custom 24-Hour Time Picker */}
                  <div className="relative">
                    <label className="trip-calc-label">Time (24h)</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={tripTime}
                        onChange={(e) => { setTripTime(e.target.value); setBooked(false); }}
                        onClick={handleOpenTimePicker}
                        placeholder="e.g. 14:30"
                        className="trip-calc-input cursor-pointer pr-10"
                      />
                      <ClockIcon
                        onClick={handleOpenTimePicker}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gold-base cursor-pointer hover:scale-110 transition-transform"
                      />
                    </div>

                    <AnimatePresence>
                      {showTimePicker && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute left-0 top-full mt-2 w-full sm:w-72 bg-[#141414] border border-white/10 rounded-sm shadow-2xl z-50 p-5"
                        >
                          <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-3">
                            <div>
                              <span className="text-white font-bold text-xs tracking-widest uppercase block">24-Hour Clock</span>
                              <span className="text-gold-base font-mono text-sm mt-0.5 block font-bold">
                                {selectedHour}:{selectedMinute}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowTimePicker(false)}
                              className="text-white/40 hover:text-white transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-6">
                            {/* Hours Column */}
                            <div>
                              <span className="text-[10px] font-bold text-white/50 uppercase tracking-wider block mb-2 text-center">
                                Hour (00-23)
                              </span>
                              <div className="max-h-48 overflow-y-auto pr-1 space-y-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/10 [&::-webkit-scrollbar-thumb]:rounded-full">
                                {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => (
                                  <button
                                    key={h}
                                    type="button"
                                    onClick={() => setSelectedHour(h)}
                                    className={cn(
                                      "w-full py-1.5 text-xs rounded-sm font-medium transition-all text-center",
                                      selectedHour === h ? "bg-gold-base text-black font-bold" : "bg-white/5 text-white/80 hover:bg-gold-base/50 hover:text-white"
                                    )}
                                  >
                                    {h}:00
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Minutes Column */}
                            <div>
                              <span className="text-[10px] font-bold text-white/50 uppercase tracking-wider block mb-2 text-center">
                                Minute
                              </span>
                              <div className="max-h-48 overflow-y-auto pr-1 space-y-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-white/10 [&::-webkit-scrollbar-thumb]:rounded-full">
                                {Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0')).map(m => (
                                  <button
                                    key={m}
                                    type="button"
                                    onClick={() => setSelectedMinute(m)}
                                    className={cn(
                                      "w-full py-1.5 text-xs rounded-sm font-medium transition-all text-center",
                                      selectedMinute === m ? "bg-gold-base text-black font-bold" : "bg-white/5 text-white/80 hover:bg-gold-base/50 hover:text-white"
                                    )}
                                  >
                                    :{m}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setTripTime(`${selectedHour}:${selectedMinute}`);
                              setBooked(false);
                              setShowTimePicker(false);
                            }}
                            className="w-full py-3 bg-gold-base text-black font-bold text-xs uppercase tracking-widest rounded-sm hover:bg-gold-light transition-all shadow-lg shadow-gold-base/20"
                          >
                            Confirm Time
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Guests Dropdown */}
                  <div>
                    <label className="trip-calc-label">Guests</label>
                    <div className="relative">
                      <select
                        value={guests}
                        onChange={(e) => handleGuestsChange(e.target.value)}
                        className="trip-calc-input appearance-none cursor-pointer pr-10"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, "9+"].map((num) => (
                          <option key={num} value={`${num} ${num === 1 ? 'Guest' : 'Guests'}`}>
                            {num} {num === 1 ? 'Guest' : 'Guests'}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="trip-calc-label">Additional Requirements</label>
                  <textarea
                    rows={3}
                    value={requirements}
                    onChange={(e) => handleRequirementsChange(e.target.value)}
                    placeholder="Any dietary requirements, specific interests, luggage details, or accessibility needs..."
                    className="trip-calc-input resize-none w-full p-4"
                  />
                </div>

                {/* calculate button */}
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={calculate}
                  disabled={calculating || !((pickup || pickupText.trim().length >= 3) && (destination || destText.trim().length >= 3))}
                  className="trip-calc-calculate-btn"
                >
                  {calculating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                  {calculating ? 'Calculating...' : 'Calculate Fare'}
                </motion.button>

                {/* results */}
                <AnimatePresence mode="wait">
                  {price !== null && distanceKm !== null && (
                    <motion.div
                      key="result"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    >
                      <div className="trip-calc-result">
                        <div className="trip-calc-result-header">
                          <Sparkles className="w-5 h-5" />
                          <span>Your Estimated Fare</span>
                        </div>

                        <div className="trip-calc-result-body">
                          <div className="trip-calc-result-price-block">
                            <motion.p
                              initial={{ scale: 0.5, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
                              className="trip-calc-result-price"
                            >
                              R{price.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                            </motion.p>
                            <p className="trip-calc-result-vehicle-label">
                              {vehicles.find(v => v.key === vehicle)?.label} · {vehicles.find(v => v.key === vehicle)?.seats}
                            </p>
                          </div>

                          <div className="trip-calc-result-details">
                            <div className="trip-calc-result-detail">
                              <Route className="w-4 h-4" />
                              <div>
                                <span className="trip-calc-result-detail-label">Distance</span>
                                <span className="trip-calc-result-detail-value">{distanceKm} km</span>
                              </div>
                            </div>
                            {durationMin !== null && (
                              <div className="trip-calc-result-detail">
                                <Clock className="w-4 h-4" />
                                <div>
                                  <span className="trip-calc-result-detail-label">Est. Time</span>
                                  <span className="trip-calc-result-detail-value">{durationMin} min</span>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="trip-calc-booking-actions">
                            {booked ? (
                              <>
                                <motion.div
                                  initial={{ scale: 0.8, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  className="trip-calc-booked-msg"
                                >
                                  <Check className="w-5 h-5" />
                                  <span>Booking Sent!</span>
                                </motion.div>
                                <button
                                  type="button"
                                  onClick={resetCalculator}
                                  className="trip-calc-email-btn"
                                >
                                  Calculate Another
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={handleWhatsAppBooking}
                                  className="trip-calc-whatsapp-btn"
                                >
                                  <MessageCircle className="w-4 h-4" />
                                  WhatsApp
                                </button>
                                <button
                                  type="button"
                                  onClick={handleEmailBooking}
                                  className="trip-calc-email-btn"
                                >
                                  <Mail className="w-4 h-4" />
                                  Email
                                </button>
                                <button
                                  type="button"
                                  onClick={resetCalculator}
                                  className="trip-calc-email-btn bg-white/5 border-white/10 hover:bg-white/10"
                                >
                                  Reset
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {outOfRange && (
                    <motion.div
                      key="outofrange"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="trip-calc-outofrange"
                    >
                      <p>
                        {distanceKm !== null && !fetchFailed
                          ? `This route is ${distanceKm} km — beyond our standard pricing matrix. For a bespoke quote and tailored arrangements, please contact our WhatsApp line.`
                          : 'Unable to calculate route.'}
                      </p>
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4">
                        {distanceKm !== null && distanceKm > SPECIAL_QUOTE_THRESHOLD ? (
                          <button
                            type="button"
                            onClick={handleWhatsAppQuote}
                            className="trip-calc-whatsapp-btn mt-0"
                          >
                            Get a Direct Quote <ArrowRight className="w-4 h-4" />
                          </button>
                        ) : (
                          <Link to="/contact" className="trip-calc-outofrange-link mt-0">
                            Contact Us for a Custom Quote <ArrowRight className="w-4 h-4" />
                          </Link>
                        )}
                        <button
                          type="button"
                          onClick={resetCalculator}
                          className="trip-calc-email-btn py-2 px-4 text-xs"
                        >
                          Calculate Another Trip
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* carousel indicator dots */}
              <div className="trip-calc-dots">
                <span className="trip-calc-dot" />
                <span className="trip-calc-dot trip-calc-dot--active" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* rate table toggle */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-8 text-center"
        >
          <button
            onClick={() => setShowRates(!showRates)}
            className="trip-calc-rates-toggle"
          >
            View Our Rates
            <ChevronDown className={cn('w-4 h-4 transition-transform', showRates && 'rotate-180')} />
          </button>

          <AnimatePresence>
            {showRates && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4 }}
                className="overflow-hidden"
              >
                <div className="trip-calc-rates-table-wrap">
                  <table className="trip-calc-rates-table">
                    <thead>
                      <tr>
                        <th>Distance</th>
                        <th>Sedan</th>
                        <th>Luxury MPV</th>
                        <th>Minibus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rateBrackets.map((b, i) => (
                        <motion.tr
                          key={i}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.08 }}
                        >
                          <td>{b.min}–{b.max} km</td>
                          <td>R{b.sedan.toFixed(2)}</td>
                          <td>R{b.luxuryMpv.toFixed(2)}</td>
                          <td>R{b.minibus.toFixed(2)}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
